import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, AlertCircle, CheckCircle, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FollowUpCalls = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [pendingFollowUps, setPendingFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      fetchPendingFollowUps();
    }
  }, [user]);

  const fetchPendingFollowUps = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/emr/followups/pending`, { withCredentials: true });
      setPendingFollowUps(response.data);
    } catch (err) {
      setError('Failed to load pending follow-ups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePatientClick = (patientId) => {
    navigate(`/emr/patient/${patientId}`);
  };

  if (!user) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-yellow-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate('/emr')}
            className="flex items-center space-x-2 text-gray-400 hover:text-yellow-400 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-yellow-400 flex items-center">
                <Phone className="w-8 h-8 mr-3" />
                Next-Day Follow-Up Calls
              </h1>
              <p className="text-gray-400 mt-1">Patients requiring follow-up after IV infusion</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Role Info */}
        <div className="mb-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <p className="text-blue-400">
            {user.role === 'admin' 
              ? '👑 Admin View: Showing all patients and their assigned RNs' 
              : '👨‍⚕️ Staff View: Showing only your assigned patients'}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading pending follow-ups...</p>
          </div>
        ) : pendingFollowUps.length === 0 ? (
          /* No Pending Follow-ups */
          <div className="text-center py-12 bg-gray-900 border border-green-500/20 rounded-xl">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-green-400 mb-2">All Caught Up! 🎉</h3>
            <p className="text-gray-400">No pending follow-up calls at the moment.</p>
          </div>
        ) : (
          /* Pending Follow-ups List */
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">
                {pendingFollowUps.length} Patient{pendingFollowUps.length !== 1 ? 's' : ''} Requiring Follow-Up
              </h2>
            </div>

            <div className="space-y-4">
              {pendingFollowUps.map((followup) => (
                <div
                  key={followup.visit_id}
                  className="bg-gray-900 border-2 border-red-500 rounded-xl p-6 hover:bg-gray-800 transition-colors cursor-pointer group"
                  onClick={() => handlePatientClick(followup.patient_id)}
                >
                  {/* Red Alert Badge */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <div>
                        <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors">
                          {followup.patient_name}
                        </h3>
                        <p className="text-sm text-gray-400 flex items-center mt-1">
                          <Phone className="w-4 h-4 mr-1" />
                          {followup.patient_phone}
                        </p>
                      </div>
                    </div>
                    <span className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg font-semibold text-sm border border-red-500/30">
                      ⚠️ NEEDS ATTENTION
                    </span>
                  </div>

                  {/* Visit Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 bg-gray-800/50 rounded-lg p-4">
                    <div>
                      <label className="text-xs text-gray-400 font-medium">IV Treatment</label>
                      <p className="text-white font-medium">{followup.iv_cocktail}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 font-medium">Visit Date</label>
                      <p className="text-white font-medium">
                        {new Date(followup.visit_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 font-medium">Assigned RN</label>
                      <p className="text-white font-medium flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        {followup.documented_by_name}
                      </p>
                    </div>
                  </div>

                  {/* Action Instruction */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                    <p className="text-gray-400 text-sm">
                      Click to open patient chart and document follow-up call
                    </p>
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 rounded border-2 border-gray-600 group-hover:border-yellow-400 transition-colors"></div>
                      <span className="text-sm text-gray-400">Mark as completed in chart</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Instructions */}
            <div className="mt-8 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-yellow-400 mb-3">📋 Follow-Up Process</h3>
              <ol className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <span className="font-bold text-yellow-400 mr-2">1.</span>
                  <span>Click on a patient's card to open their full medical chart</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-yellow-400 mr-2">2.</span>
                  <span>Expand the "IV Infusion F/U Call" section</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-yellow-400 mr-2">3.</span>
                  <span>Document your conversation with the patient (free text)</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-yellow-400 mr-2">4.</span>
                  <span>Click "Save Follow-Up Call" - this will be marked as "Completed by [Your Name] BSN.RN"</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-yellow-400 mr-2">5.</span>
                  <span>Patient will disappear from this alert list once follow-up is documented</span>
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowUpCalls;
